import { defineStore } from 'pinia';
import { getAllCategories, addDepartmentAPI, deleteDepartmentAPI, updateDepartmentAPI, getAllDepartments } from '@/utils/api';
export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
}

export interface ViviposCategory {
    MID: number;
    ID: string;
    No?: string | null;
    Name?: string | null;
    Rate?: string | null;
    ButtonColor?: string | null;
    FontSize?: string | null;
    Visible?: boolean | null;
    CanSale?: boolean | null;
    Created?: number | null;
    Modified?: number | null;
    DisplayOrder?: number | null;
    SaleUnit?: string | null;
    Scale?: boolean | null;
    NonDiscountable?: boolean | null;
    NonSurchargeable?: boolean | null;
    AltLanguage?: string | null;
    PicThumb?: string | null;
}

export interface Department {
    id: string;
    no: string;
    name: string;
    showOnMenu: boolean;
    saleDepartment: boolean;
    taxRate: string;
    displayOrder: number;
    nonDiscountable: boolean;
    nonSurchargeable: boolean;
    scaleDepartment: boolean;
    unitOfSale: string;
    appearance: Appearance;
    altDisplayLang: String[];
}

// Converts Department to ViviposCategory
export function departmentToViviposCategory(department: Department): ViviposCategory {
    return {
        MID: 1, // Set as needed
        ID: department.id,
        No: department.no,
        Name: department.name,
        Visible: department.showOnMenu,
        CanSale: department.saleDepartment,
        Rate: department.taxRate,
        DisplayOrder: department.displayOrder,
        NonDiscountable: department.nonDiscountable,
        NonSurchargeable: department.nonSurchargeable,
        Scale: department.scaleDepartment,
        SaleUnit: department.unitOfSale,
        PicThumb: department.appearance?.image ?? '',
        ButtonColor: department.appearance?.buttonColor ?? '',
        FontSize: department.appearance?.fontSizes ?? '',
        AltLanguage: department.altDisplayLang ? department.altDisplayLang.join('||') : ''
    };
}

// Converts ViviposCategory to Department
export function viviposCategoryToDepartment(category: ViviposCategory): Department {
    return {
        id: category.ID,
        no: category.No ?? '',
        name: category.Name ?? '',
        showOnMenu: category.Visible ?? false,
        saleDepartment: category.CanSale ?? false,
        taxRate: category.Rate ?? '',
        displayOrder: category.DisplayOrder ?? 0,
        nonDiscountable: category.NonDiscountable ?? false,
        nonSurchargeable: category.NonSurchargeable ?? false,
        scaleDepartment: category.Scale ?? false,
        unitOfSale: category.SaleUnit ?? '',
        appearance: {
            image: category.PicThumb ?? '',
            buttonColor: category.ButtonColor ?? '',
            fontSizes: category.FontSize ?? '',
        },
        altDisplayLang: category.AltLanguage ? category.AltLanguage.split('||') : [],
    };
}

export const defaultDepartment = {
    id: '',
    no: '',
    name: '',
    showOnMenu: false,
    saleDepartment: false,
    taxRate: '',
    displayOrder: 0,
    nonDiscountable: false,
    nonSurchargeable: false,
    scaleDepartment: false,
    unitOfSale: '',
    appearance: {
        image: '',
        buttonColor: '',
        fontSizes: '',
    },
    altDisplayLang: []
} as Department;

export const useDepartmentsStore = defineStore('departments', {
    state: () => ({
        departmentList: [] as Department[],
        activeDepartment: {} as Department,
        tempActiveDepartment: {} as Department,
    }),
    getters: {
        getActiveDepartment: (state) => {
            return state.activeDepartment;
        },
        getActiveDepartmentId: (state) => {
            return state.activeDepartment.id;
        },
        findDepartmentById: (state) => (id: string) => {
            return state.departmentList.find(department => department.id === id);
        },
        getDepartmentsList: (state) => {
            return state.departmentList;
        },
        getTempActiveDepartment: (state) => {
            return state.tempActiveDepartment;
        },
        getIsActiveDepartmentChanged: (state) => {
            return JSON.stringify(state.activeDepartment) !== JSON.stringify(state.tempActiveDepartment);
        }
    },
    actions: {
        initializeDepartments() {
            getAllDepartments().then((response: Department[]) => {
                this.departmentList = response.map((item: any) => viviposCategoryToDepartment(item));
            }).catch((error: Error) => {
                console.error('Error fetching categories:', error);
            });
        },
        addDepartment(department: Department) {
            addDepartmentAPI(department).then((response) => {
                getAllCategories().then((response: Department[]) => {
                    this.departmentList = response;
                    this.activeDepartment = department;
                    this.tempActiveDepartment = department;
                }
                ).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                }
                );
            }).catch((error) => {
                console.error('Error adding department:', error);
            });
        },
        removeDepartment(id: string) {
            deleteDepartmentAPI(id).then(() => {
                getAllCategories().then((response: Department[]) => {
                    this.departmentList = response;
                    this.activeDepartment = defaultDepartment;
                    this.tempActiveDepartment = defaultDepartment;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error) => {
                console.error('Error deleting department:', error);
            });
        },
        updateDepartment(updatedDepartment: Department) {
            updateDepartmentAPI(updatedDepartment).then(() => {
                getAllCategories().then((response: Department[]) => {
                    this.departmentList = response;
                    this.activeDepartment = updatedDepartment;
                    this.tempActiveDepartment = updatedDepartment;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error) => {
                console.error('Error updating department:', error);
            });
        },
        setActiveDepartment(department: Department) {
            this.activeDepartment = department;
        },
        setTempActiveDepartment(department: Department) {
            this.tempActiveDepartment = department;
        },
        initActiveDepartment(department: Department) {
            this.activeDepartment = department;
            this.tempActiveDepartment = department;
        }

    },
});